import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import styled from "styled-components";

import { getS3FileURL } from "@examspark/common";
import {
  useMutationDeleteClassroomResource,
  useStickyState,
} from "@examspark/common-app";
import {
  Button,
  ConfirmModal,
  FlexBox,
  Grid,
  Modal,
  SwitchButton,
  toast,
  Toolbar,
} from "@examspark/common-ui";
import {
  CardLink,
  LinkCard,
} from "@examspark/common-ui/dist/components/containers/LinkCard";
import {
  IconDocumentLine,
  IconLinkLine,
  IconTrashLine,
} from "@examspark/common-ui/dist/components/icons";
import { Resource, ResourceFile } from "@examspark/types";

import { getLinkForResource } from "../../utils";
import BrowseList from "../browse/BrowseList";
import PinToPlannerButton from "../classroom/PinToPlannerButton";
import WorkspaceBadge from "../common/WorkspaceBadge";

const ItemImage = styled.div`
  img {
    border-radius: 0.3rem 0.3rem 0 0;
    max-width: 100%;
  }
`;
const ItemBody = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;
const ItemBodyMain = styled.div`
  height: 15rem;
`;
const ItemDescription = styled.div`
  font-size: 1.1rem;
  color: #444;
  max-height: 5rem;
  line-height: 1.5em;
  overflow: hidden;
  margin: 0.5rem 0 1rem;
`;
const ItemFooter = styled.div`
  margin-top: auto;
`;

const ItemIconHero = styled.div`
  height: 10rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f2f2f2;
  border-radius: 0.3rem 0.3rem 0 0;
  position: relative;

  svg {
    fill: #888;
    height: 5rem;
  }
`;
const StyledLinkCard = styled(LinkCard)`
  padding: 2px;
`;

const StyledImg = styled.img`
  width: 100%;
  height: 10rem;
  object-fit: cover;
  object-position: center;
`;

const FloatingBadge = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
`;

const ResourcesList = (props: {
  resources: Resource[];

  // passing in a classroomId is our hint that we're showing
  // "my resources" and should show the delete button
  classroomId?: string;

  view?: "list" | "grid";
}) => {
  const mutationDeleteResource = useMutationDeleteClassroomResource();
  const queryClient = useQueryClient();

  const [view, setView] = useStickyState<"list" | "grid">(
    "resourceListView",
    props.view ?? "list",
  );

  const sortedResources = props.resources.sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? ""),
  );

  const onDelete = async (item: Resource) => {
    const shouldContinue = await ConfirmModal.show({
      title: "Delete Resource",
      confirmLabel: "Delete",
      confirmColor: "danger",
      children: (
        <Modal.PaddedBody>
          Are you sure you want to delete <em>{item.name}</em>? This action
          cannot be undone.
        </Modal.PaddedBody>
      ),
    });

    if (!shouldContinue) return;

    try {
      await mutationDeleteResource.mutateAsync(item);
      toast.success("Resource deleted");

      queryClient.setQueriesData(
        ["classroom", props.classroomId, "resources"],
        (old: Resource[] | undefined) => {
          if (!old) return old;

          return old.filter((r) => r.id !== item.id);
        },
      );
    } catch (err) {
      console.error(err);
      toast.error("Error deleting resource");
    }
  };

  return (
    <FlexBox>
      {props.view == null && (
        <Toolbar.Right>
          <SwitchButton
            label="List View"
            checked={view === "list"}
            onClick={() => setView(view === "list" ? "grid" : "list")}
          />
        </Toolbar.Right>
      )}
      {view === "list" && <BrowseList data={sortedResources} />}
      {view === "grid" && (
        <Grid>
          {sortedResources.map((item) => (
            <ResourceListGridCard
              key={item.id}
              item={item}
              onDelete={onDelete}
              classroomId={props.classroomId}
            />
          ))}
        </Grid>
      )}
    </FlexBox>
  );
};

const ResourceListGridCard = ({
  item,
  onDelete,
  classroomId,
}: {
  item: Resource;
  onDelete: (item: Resource) => void;
  classroomId?: string;
}) => {
  // document `file` for thumbnail
  const fileUrl = getS3FileURL(item.file as ResourceFile);

  const openInNewTab = item.type === "EXTERNAL_LINK";

  return (
    <StyledLinkCard key={item.id}>
      {item.type === "DOCUMENT" && (
        <ItemIconHero>
          <div
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconDocumentLine title="Document" />
          </div>
          <div
            style={{
              backgroundImage: `url("${fileUrl}-thumb.png")`,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              height: "10rem",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
            }}
          />
        </ItemIconHero>
      )}
      {(item.type === "PERFORMANCE_TASK" ||
        item.type === "WEB_DOCUMENT" ||
        item.type === "PASSAGE" ||
        item.type === "RUBRIC") && (
        <ItemIconHero>
          <IconDocumentLine title="Document" />
        </ItemIconHero>
      )}
      {item.type === "EXTERNAL_LINK" && (
        <ItemImage>
          {item.imageUrl ? (
            <StyledImg src={item.imageUrl} alt={item.name ?? ""} />
          ) : (
            <ItemIconHero>
              <IconLinkLine title="External Link" />
            </ItemIconHero>
          )}
        </ItemImage>
      )}
      {item.type === "IMAGE" && item.file && (
        <ItemImage>
          <StyledImg src={getS3FileURL(item.file)} alt={item.name ?? ""} />
        </ItemImage>
      )}
      <FloatingBadge>
        {item.workspace && <WorkspaceBadge workspace={item.workspace} />}
      </FloatingBadge>
      <ItemBody>
        <ItemBodyMain>
          <CardLink
            to={getLinkForResource(item) ?? "#"}
            target={openInNewTab ? "_blank" : undefined}
          >
            {item.name}
          </CardLink>
          <ItemDescription>{item.description}</ItemDescription>
        </ItemBodyMain>
        <ItemFooter>
          <Toolbar.Right style={{ justifyContent: "end" }}>
            <PinToPlannerButton resource={item} />
            {classroomId && (
              <Button title="Delete Resource" onClick={() => onDelete(item)}>
                <IconTrashLine width={16} />
              </Button>
            )}
          </Toolbar.Right>
        </ItemFooter>
      </ItemBody>
    </StyledLinkCard>
  );
};

export default ResourcesList;
